import React, { useEffect, useRef, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useWallet } from "@solana/wallet-adapter-react";

import { createUmi } from "@metaplex-foundation/umi-bundle-defaults";
import { base58 } from "@metaplex-foundation/umi/serializers";
import { publicKey } from "@metaplex-foundation/umi";
import { walletAdapterIdentity } from "@metaplex-foundation/umi-signer-wallet-adapters";

import {
    mplTokenMetadata,
    fetchDigitalAsset,
    burnV1,
    findMetadataPda,
    TokenStandard,
} from "@metaplex-foundation/mpl-token-metadata";
import {
    burn as burnCore,
    fetchAsset,
    collectionAddress,
    fetchCollection,
} from "@metaplex-foundation/mpl-core";
import {
    mplBubblegum,
    getAssetWithProof,
    burnV2,
} from "@metaplex-foundation/mpl-bubblegum";

import * as htmlToImage from "html-to-image";


import "./SimulacrumExplorerStyle.css";
import ImageModal from "./ImageModal";

function TokenDetail() {
    const { mint } = useParams();                    // /token/:mint
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const wallet = useWallet();

    const walletAddressStr = wallet.publicKey?.toBase58() || null;
    const ownerFromState = location.state?.owner || null;

// Only allow burn when we *know* the owner and it matches the connected wallet
    const canBurn = !!walletAddressStr && !!ownerFromState && walletAddressStr === ownerFromState;


    const [styleMode] = useState(
        () => localStorage.getItem("walletExplorerStyle") || "og-retro"
    );

    const [modalImage, setModalImage] = useState(null);
    const [sharingTokenMint, setSharingTokenMint] = useState(null);
    const [burnInfo, setBurnInfo] = useState(null);
    const [loadingToken, setLoadingToken] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [token, setToken] = useState(() => location.state?.token || null);

    const cardRef = useRef(null);

    // --------------------------------------
    // Helpers for mapping assets → token shape
    // (copied from WalletExplorer so detail matches list)
    // --------------------------------------
    const endpoint = process.env.REACT_APP_RPC_ENDPOINT;

    const mapDigitalAssetToToken = async (asset, umi) => {
        const mintAddress = asset.mint.publicKey.toString();
        const metadataUri = asset.metadata?.uri || null;
        const collectionKey = asset.metadata?.collection?.value?.key || null;
        const amount = asset.amount;

        let metadata = null;
        let imageUrl = null;
        let externalUrl = null;

        if (metadataUri) {
            try {
                const response = await fetch(metadataUri);
                metadata = await response.json();
                imageUrl = metadata?.image || null;
                externalUrl = metadata?.external_url || null;
            } catch (e) {
                // ignore
            }
        }

        const files = metadata?.properties?.files || [];
        const categoryRaw = metadata?.properties?.category || "";

        const findByMimePrefix = (prefix) => {
            if (!Array.isArray(files) || !files.length) return null;
            return (
                files.find((f) => {
                    const mime = (f.type || f.mime || "").toLowerCase();
                    return mime.startsWith(prefix);
                }) || null
            );
        };

        const imageFile = findByMimePrefix("image/");
        const videoFile = findByMimePrefix("video/");
        const audioFile = findByMimePrefix("audio/");

        if (!imageUrl && imageFile && imageFile.uri) {
            imageUrl = imageFile.uri;
        }

        let mediaCategory = categoryRaw.toLowerCase();
        if (!mediaCategory) {
            if (videoFile) mediaCategory = "video";
            else if (audioFile) mediaCategory = "audio";
            else if (imageFile) mediaCategory = "image";
            else mediaCategory = "";
        }

        let animationUrl = null;
        let audioUrl = null;
        const rawAnimationUrl = metadata?.animation_url || null;

        if (mediaCategory === "video") {
            animationUrl =
                rawAnimationUrl ||
                (videoFile && videoFile.uri) ||
                null;
            if (audioFile && audioFile.uri) {
                audioUrl = audioFile.uri;
            }
        } else if (mediaCategory === "audio") {
            audioUrl =
                rawAnimationUrl ||
                (audioFile && audioFile.uri) ||
                null;
        } else {
            if (videoFile && videoFile.uri) {
                animationUrl = videoFile.uri;
            }
            if (!animationUrl && rawAnimationUrl) {
                const lower = rawAnimationUrl.toLowerCase();
                if (lower.includes("ext=mp4") || lower.endsWith(".mp4")) {
                    animationUrl = rawAnimationUrl;
                } else if (lower.includes("ext=mp3") || lower.endsWith(".mp3")) {
                    audioUrl = rawAnimationUrl;
                }
            }
            if (!audioUrl && audioFile && audioFile.uri) {
                audioUrl = audioFile.uri;
            }
        }

        const attributes = Array.isArray(metadata?.attributes)
            ? metadata.attributes
            : [];

        return {
            mint: mintAddress,
            name: metadata?.name || "Unknown",
            amount,
            type: "NFT",
            description: metadata?.description || "",
            symbol: metadata?.symbol || "Unknown",
            uri: metadataUri || "No Metadata URI",
            collectionKey: collectionKey || null,
            image: imageUrl || "No image URL",
            external_url: externalUrl || null,
            animationUrl: animationUrl || null,
            audioUrl: audioUrl || null,
            mediaType: mediaCategory,
            attributes,
        };
    };

    const mapCoreDigitalAssetToToken = async (asset) => {
        const mintAddress = asset.publicKey;
        const metadataUri = asset.uri || null;
        const collectionKey = asset.collection?.key || null;
        const amount = asset.amount;

        let metadata = null;
        let imageUrl = null;
        let externalUrl = null;

        if (metadataUri) {
            try {
                const response = await fetch(metadataUri);
                metadata = await response.json();
                imageUrl = metadata?.image || null;
                externalUrl = metadata?.external_url || null;
            } catch (e) {
                // ignore
            }
        }

        const files = metadata?.properties?.files || [];
        const categoryRaw = metadata?.properties?.category || "";

        const findByMimePrefix = (prefix) => {
            if (!Array.isArray(files) || !files.length) return null;
            return (
                files.find((f) => {
                    const mime = (f.type || f.mime || "").toLowerCase();
                    return mime.startsWith(prefix);
                }) || null
            );
        };

        const imageFile = findByMimePrefix("image/");
        const videoFile = findByMimePrefix("video/");
        const audioFile = findByMimePrefix("audio/");

        if (!imageUrl && imageFile && imageFile.uri) {
            imageUrl = imageFile.uri;
        }

        let mediaCategory = categoryRaw.toLowerCase();
        if (!mediaCategory) {
            if (videoFile) mediaCategory = "video";
            else if (audioFile) mediaCategory = "audio";
            else if (imageFile) mediaCategory = "image";
            else mediaCategory = "";
        }

        let animationUrl = null;
        let audioUrl = null;
        const rawAnimationUrl = metadata?.animation_url || null;

        if (mediaCategory === "video") {
            animationUrl =
                rawAnimationUrl ||
                (videoFile && videoFile.uri) ||
                null;
            if (audioFile && audioFile.uri) {
                audioUrl = audioFile.uri;
            }
        } else if (mediaCategory === "audio") {
            audioUrl =
                rawAnimationUrl ||
                (audioFile && audioFile.uri) ||
                null;
        } else {
            if (videoFile && videoFile.uri) {
                animationUrl = videoFile.uri;
            }
            if (!animationUrl && rawAnimationUrl) {
                const lower = rawAnimationUrl.toLowerCase();
                if (lower.includes("ext=mp4") || lower.endsWith(".mp4")) {
                    animationUrl = rawAnimationUrl;
                } else if (lower.includes("ext=mp3") || lower.endsWith(".mp3")) {
                    audioUrl = rawAnimationUrl;
                }
            }
            if (!audioUrl && audioFile && audioFile.uri) {
                audioUrl = audioFile.uri;
            }
        }

        const attributes = Array.isArray(metadata?.attributes)
            ? metadata.attributes
            : [];

        return {
            mint: mintAddress,
            name: asset.name || metadata?.name || "Unknown",
            amount,
            type: "Core NFT",
            description: metadata?.description || "",
            symbol: asset.data?.symbol || metadata?.symbol || "Unknown",
            uri: metadataUri || "No Metadata URI",
            collectionKey: asset.updateAuthority?.address || collectionKey || null,
            image: imageUrl || "No image URL",
            external_url: externalUrl || null,
            animationUrl: animationUrl || null,
            audioUrl: audioUrl || null,
            mediaType: mediaCategory,
            attributes,
        };
    };

    const mapCompressedAssetToToken = async (asset) => {
        const assetId = asset.id;
        const jsonUri = asset.content?.json_uri || null;

        let metadata = asset.content?.metadata || null;
        let imageUrl = asset.content?.links?.image || null;
        let externalUrl =
            metadata?.external_url || asset.content?.links?.external || null;

        const needsRemoteMetadata =
            jsonUri &&
            (
                !metadata ||
                !metadata.image ||
                !metadata.animation_url &&
                !(
                    metadata.properties &&
                    Array.isArray(metadata.properties.files) &&
                    metadata.properties.files.length
                )
            );

        if (needsRemoteMetadata) {
            try {
                const response = await fetch(jsonUri);
                const fetchedMetadata = await response.json();
                metadata = metadata ? { ...metadata, ...fetchedMetadata } : fetchedMetadata;

                if (!imageUrl) imageUrl = fetchedMetadata.image || null;
                if (!externalUrl) externalUrl = fetchedMetadata.external_url || null;
            } catch (e) {
                // ignore
            }
        }

        const filesFromMetadata = metadata?.properties?.files || [];
        const filesFromContent = asset.content?.files || [];
        const allFiles = [...filesFromMetadata, ...filesFromContent];

        let animationUrl =
            metadata?.animation_url ||
            asset.content?.links?.animation_url ||
            null;

        if (!animationUrl && allFiles.length) {
            const videoFile = allFiles.find((f) => {
                const mime = (f.type || f.mime || "").toLowerCase();
                return mime.startsWith("video/");
            });
            if (videoFile) animationUrl = videoFile.uri || null;
        }

        let audioUrl = null;
        if (allFiles.length) {
            const audioFile = allFiles.find((f) => {
                const mime = (f.type || f.mime || "").toLowerCase();
                return mime.startsWith("audio/");
            });
            if (audioFile) audioUrl = audioFile.uri || null;
        }

        if (!imageUrl && allFiles.length) {
            const imageFile =
                allFiles.find((f) => {
                    const mime = (f.type || f.mime || "").toLowerCase();
                    return mime.startsWith("image/");
                }) || allFiles[0];
            imageUrl = imageFile?.uri || null;
        }

        const collectionGrouping =
            asset.grouping?.find((g) => g.group_key === "collection") || null;

        const mediaCategory =
            (metadata?.properties?.category ||
                asset.content?.metadata?.properties?.category ||
                asset.content?.metadata?.category ||
                "").toLowerCase();

        const baseDescription =
            metadata?.description ||
            asset.content?.metadata?.description ||
            "";

        const attributes =
            Array.isArray(metadata?.attributes)
                ? metadata.attributes
                : Array.isArray(asset.content?.metadata?.attributes)
                ? asset.content.metadata.attributes
                : [];

        return {
            mint: assetId,
            assetId,
            name:
                metadata?.name ||
                asset.content?.metadata?.name ||
                "Unknown",
            amount: 1,
            type: "Compressed NFT",
            description: baseDescription,
            mediaType: mediaCategory,
            symbol:
                metadata?.symbol ||
                asset.content?.metadata?.symbol ||
                "Unknown",
            uri: jsonUri || "No Metadata URI",
            collectionKey: collectionGrouping?.group_value || null,
            image: imageUrl || "No image URL",
            external_url: externalUrl || null,
            animationUrl: animationUrl || null,
            audioUrl: audioUrl || null,
            attributes,
        };
    };

    // --------------------------------------
    // Fetch single token by mint/asset id
    // --------------------------------------
    useEffect(() => {
        // If we already have a token (came from WalletExplorer via state),
        // no need to fetch.
        if (token) return;
        if (!mint) return;

        let cancelled = false;

        const load = async () => {
            setLoadingToken(true);
            setLoadError(null);

            try {
                // 1) Try regular NFT via Token Metadata
                const umi = createUmi(endpoint).use(mplTokenMetadata());
                const mintPk = publicKey(mint);

                try {
                    const asset = await fetchDigitalAsset(umi, mintPk);
                    const mapped = await mapDigitalAssetToToken(asset, umi);
                    if (!cancelled) {
                        setToken(mapped);
                        queryClient.setQueryData(["tokenByMint", mint], mapped);
                        return;
                    }
                } catch (e) {
                    // swallow, then try core/ compressed
                }

                // 2) Try Core NFT
                try {
                    const umiCore = createUmi(endpoint);
                    const assetCore = await fetchAsset(umiCore, mintPk);
                    const mappedCore = await mapCoreDigitalAssetToToken(assetCore);
                    if (!cancelled) {
                        setToken(mappedCore);
                        queryClient.setQueryData(["tokenByMint", mint], mappedCore);
                        return;
                    }
                } catch (e) {
                    // swallow
                }

                // 3) Try DAS getAsset for compressed NFT
                try {
                    const dasBody = {
                        jsonrpc: "2.0",
                        id: "getAsset",
                        method: "getAsset",
                        params: {
                            id: mint,
                        },
                    };

                    const resp = await fetch(endpoint, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(dasBody),
                    });
                    const json = await resp.json();
                    if (json.error) throw new Error(json.error.message || "DAS error");
                    const asset = json.result;
                    const mappedCompressed = await mapCompressedAssetToToken(asset);
                    if (!cancelled) {
                        setToken(mappedCompressed);
                        queryClient.setQueryData(["tokenByMint", mint], mappedCompressed);
                        return;
                    }
                } catch (e) {
                    // swallow
                }

                if (!cancelled) {
                    setLoadError("Could not find this token on-chain.");
                }
            } catch (err) {
                if (!cancelled) {
                    console.error("Failed to load token by mint:", err);
                    setLoadError(err.message || String(err));
                }
            } finally {
                if (!cancelled) setLoadingToken(false);
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [mint, endpoint, token, queryClient]);

    // --------------------------------------
    // Small component reused in card
    // --------------------------------------
    const TokenImage = ({ image, name }) => (
        <img
            className="token-item-image"
            src={image}
            alt={name}
            onClick={() => setModalImage({ image, name })}
        />
    );


// --------------------------------------
// Image / share helpers
// --------------------------------------
    // --------------------------------------
// Image / share helpers
// --------------------------------------
    const generateTokenCardImage = async (tokenObj) => {
        const cardEl = cardRef.current;
        if (!cardEl) return null;

        const fileName = `${(tokenObj.name || "nft")
            .replace(/[^\w\-]+/g, "_")}-crumb.png`;

        const bg = window.getComputedStyle(cardEl).backgroundColor || null;

        // 1) Get a PNG data URL from html-to-image
        const dataUrl = await htmlToImage.toPng(cardEl, {
            backgroundColor: bg,
            pixelRatio: 2,
        });

        // 2) Convert the data URL to a Blob
        const response = await fetch(dataUrl);
        const blob = await response.blob();

        const file = new File([blob], fileName, { type: "image/png" });

        return { file, blob, fileName, dataUrl };
    };





    const handleDownloadTokenCard = async (tokenObj) => {
        try {
            setSharingTokenMint(tokenObj.mint);

            const result = await generateTokenCardImage(tokenObj);
            if (!result) return;

            const { blob, fileName } = result;

            const urlObj = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = urlObj;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(urlObj);
        } catch (err) {
            console.error("Download failed:", err);
            alert("Could not download card.");
        } finally {
            setSharingTokenMint(null);
        }
    };


    const handleCopyTokenCard = async (tokenObj) => {
        try {
            setSharingTokenMint(tokenObj.mint);

            const result = await generateTokenCardImage(tokenObj);
            if (!result) return;

            const { blob } = result;

            if (
                navigator.clipboard &&
                window.ClipboardItem
            ) {
                await navigator.clipboard.write([
                    new ClipboardItem({ "image/png": blob }),
                ]);
                alert("Card copied to clipboard!");
            } else {
                alert("Copying images is not supported on this browser/device.");
            }
        } catch (err) {
            console.error("Copy failed:", err);
            alert("Copy not supported on this device/browser.");
        } finally {
            setSharingTokenMint(null);
        }
    };


    const handleCopyLink = async () => {
        try {
            const url = window.location.href;
            await navigator.clipboard.writeText(url);
            alert("Detail page link copied!");
        } catch (err) {
            console.error("Copy link failed:", err);
            alert("Could not copy link on this device/browser.");
        }
    };

    const handleShareTokenCard = async (tokenObj, platform) => {
        const isMobile = /Android|iPhone|iPad|iPod/i.test(
            navigator.userAgent || ""
        );

        try {
            setSharingTokenMint(tokenObj.mint);

            // Reuse the html-to-image helper
            const result = await generateTokenCardImage(tokenObj);
            if (!result) return;

            const { blob, file, fileName } = result;

            // You can change this to the detail URL if you prefer:
            // const pageUrl = `${window.location.origin}/token/${tokenObj.mint}`;
            const pageUrl = window.location.href;

            const shareText =
                `Viewing "${tokenObj.name || "this NFT"}" in The Crumb – ` +
                `NFT Wallet Explorer.`;
            const encodedText = encodeURIComponent(shareText);
            const encodedUrl = encodeURIComponent(pageUrl);

            // --- MOBILE: Web Share API with file (ideal for TWA / phones) ---
            if (
                isMobile &&
                navigator.canShare &&
                navigator.canShare({ files: [file] })
            ) {
                await navigator.share({
                    files: [file],
                    title: tokenObj.name || "My NFT",
                    text: `${shareText} ${pageUrl}`,
                });
                return;
            }

            // --- DESKTOP / fallback: download + open social composer ---

            // 1) Download the image so they can attach it manually
            const urlObj = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = urlObj;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(urlObj);

            // 2) Open the appropriate social network with prefilled text/url
            let socialUrl = null;
            switch (platform) {
                case "twitter":
                    socialUrl =
                        `https://twitter.com/intent/tweet?text=${encodedText}` +
                        `&url=${encodedUrl}`;
                    break;
                case "facebook":
                    socialUrl =
                        `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}` +
                        `&quote=${encodedText}`;
                    break;
                case "threads":
                    socialUrl =
                        `https://www.threads.net/intent/post?text=${encodedText}%20${encodedUrl}`;
                    break;
                case "instagram":
                    // No official web intent – just open Instagram
                    socialUrl = "https://www.instagram.com/";
                    break;
                default:
                    break;
            }

            if (socialUrl) {
                window.open(socialUrl, "_blank", "noopener,noreferrer");
            }
        } catch (err) {
            console.error("Error sharing token card:", err);
            // Optional: alert if you want user feedback
            // alert("Could not generate a shareable image for this token.");
        } finally {
            setSharingTokenMint(null);
        }
    };


    // --------------------------------------
    // Burn helpers
    // --------------------------------------
    const sendTxWithConfirmation = async (txBuilder, umi) => {
        const { signature } = await txBuilder.sendAndConfirm(umi);
        const [txId] = base58.deserialize(signature);
        console.log("Transaction sent and confirmed with txId:", txId);
        return txId;
    };

    const burnDigitalAsset = async (tokenObj) => {
        const umi = createUmi(endpoint).use(mplTokenMetadata());
        umi.use(walletAdapterIdentity(wallet));

        const mintPk = publicKey(tokenObj.mint);
        const owner = wallet.publicKey;

        let burnInstruction;
        if (tokenObj.collectionKey) {
            burnInstruction = await burnV1(umi, {
                mint: mintPk,
                authority: owner,
                tokenOwner: owner,
                collectionMetadata: findMetadataPda(umi, {
                    mint: publicKey(tokenObj.collectionKey),
                }),
                tokenStandard: TokenStandard.NonFungible,
            });
        } else {
            burnInstruction = await burnV1(umi, {
                mint: mintPk,
                authority: owner,
                tokenOwner: owner,
                tokenStandard: TokenStandard.NonFungible,
            });
        }

        const txId = await sendTxWithConfirmation(burnInstruction, umi);
        console.log("Digital asset burn transaction sent:", txId);
        return txId;
    };

    const burnCompressedAsset = async (tokenObj) => {
        const umi = createUmi(endpoint)
            .use(walletAdapterIdentity(wallet))
            .use(mplBubblegum());

        const rawCall = umi.rpc.call.bind(umi.rpc);
        umi.rpc.call = (...args) => {
            const [method, params, ...rest] = args;
            let normalizedParams;
            if (params === undefined) {
                normalizedParams = [];
            } else if (Array.isArray(params)) {
                normalizedParams = params;
            } else {
                normalizedParams = [params];
            }
            return rawCall(method, normalizedParams, ...rest);
        };

        const assetIdStr = tokenObj.assetId || tokenObj.id || tokenObj.mint;
        if (typeof assetIdStr !== "string" || !assetIdStr) {
            console.error("burnCompressedAsset: missing asset id:", tokenObj);
            throw new Error("Compressed NFT is missing a valid asset id string.");
        }

        const assetId = publicKey(assetIdStr);
        const assetWithProof = await getAssetWithProof(umi, assetId, {
            truncateCanopy: true,
        });

        const leafOwner = publicKey(wallet.publicKey.toBase58());

        const burnBuilder = burnV2(umi, {
            ...assetWithProof,
            leafOwner,
        });

        const txSig = await burnBuilder.sendAndConfirm(umi);
        console.log("Compressed asset burn transaction sent:", txSig);
        return txSig;
    };

    const burnCoreAsset = async (tokenObj) => {
        const umi = createUmi(endpoint);
        umi.use(walletAdapterIdentity(wallet));

        const asset = await fetchAsset(umi, publicKey(tokenObj.mint));
        let collection = undefined;
        const collectionId = collectionAddress(asset);
        if (collectionId) {
            collection = await fetchCollection(umi, collectionId);
        }

        const burnInstruction = await burnCore(umi, {
            asset,
            collection,
        });
        const txId = await sendTxWithConfirmation(burnInstruction, umi);
        console.log("Core asset burn transaction sent:", txId);
        return txId;
    };

    const handleBurn = async (tokenObj) => {
        if (!wallet || !wallet.publicKey) {
            alert("Wallet not connected.");
            return;
        }
        if (
            !window.confirm(
                `Are you sure you want to burn ${tokenObj.name || "this token"}?`
            )
        )
            return;

        setBurnInfo({ status: "Burn in progress...", tokenName: tokenObj.name });
        try {
            let txId;
            if (tokenObj.type === "NFT") {
                txId = await burnDigitalAsset(tokenObj);
            } else if (tokenObj.type === "Core NFT") {
                txId = await burnCoreAsset(tokenObj);
            } else if (tokenObj.type === "Compressed NFT") {
                txId = await burnCompressedAsset(tokenObj);
            }

            // Clear all token caches since we don't know which wallet cache contained it
            Object.keys(localStorage).forEach((key) => {
                if (key.startsWith("tokens_")) {
                    localStorage.removeItem(key);
                }
            });
            queryClient.invalidateQueries({ queryKey: ["tokens"] });

            setBurnInfo({
                status: "Burn successful",
                tokenName: tokenObj.name,
                txId,
            });
            alert("Token burned successfully.");
        } catch (error) {
            console.error("Error burning token:", error);
            setBurnInfo({
                status: "Burn failed",
                tokenName: tokenObj.name,
                error: error.message,
            });
            alert("Burn failed. Please try again.");
        }
    };

    // --------------------------------------
    // RENDER
    // --------------------------------------
    if (loadingToken && !token) {
        return (
            <div className="wallet-explorer-outer-root">
                <div className={`wallet-explorer-root theme-${styleMode}`}>
                    <p>Loading token details…</p>
                </div>
            </div>
        );
    }

    if (!token) {
        return (
            <div className="wallet-explorer-outer-root">
                <div className={`wallet-explorer-root theme-${styleMode}`}>
                    <button
                        className="token-detail-back-button"
                        onClick={() => navigate(-1)}
                    >
                        ← Back
                    </button>
                    <p>
                        Could not find this token
                        {loadError ? `: ${loadError}` : " in local cache or on-chain."}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="wallet-explorer-outer-root">
            <div className={`wallet-explorer-root theme-${styleMode}`}

            >
                <div
                    className="token-list-item-outer"
                    ref={cardRef}
                >
                    <div className="pokedex-light-container">
                        <div className="pokedex-light" />
                    </div>

                    <div className="token-name-container">
                        <h1 className="token-name-item">
                            <strong>{token.name}</strong>
                        </h1>
                    </div>

                    <div className="token-list-item">
                        {(token.type === "Compressed NFT" ||
                            token.type === "Core NFT" ||
                            token.type === "NFT") &&
                        (token.animationUrl || token.audioUrl) ? (
                            <>
                                <div className="media-container">
                                    {token.image !== "No image URL" && (
                                        <div className="token-item-image-container">
                                            <TokenImage
                                                image={token.image}
                                                name={token.name}
                                            />
                                        </div>
                                    )}

                                    {token.animationUrl && (
                                        <div className="token-item-image-container">
                                            <video
                                                className="token-item-image"
                                                src={token.animationUrl}
                                                controls
                                                loop
                                                muted
                                            />
                                        </div>
                                    )}

                                    {!token.animationUrl && token.audioUrl && (
                                        <div className="token-item-image-container">
                                            <audio
                                                className="token-item-audio"
                                                src={token.audioUrl}
                                                controls
                                            />
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : token.image !== "No image URL" ? (
                            <div className="token-item-image-container">
                                <TokenImage
                                    image={token.image}
                                    name={token.name}
                                />
                            </div>
                        ) : (
                            <p>No image available</p>
                        )}

                        <div className="token-item-description-container">
                            <div className="description-item">
                                <strong>Name: </strong>
                                {token.name}
                            </div>

                            <div className="description-item">
                                <strong>Type: </strong> {token.type}
                            </div>

                            <div className="description-item">
                                <strong>Media Type: </strong> {token.mediaType}
                            </div>

                            <div className="description-item">
                                <strong>Symbol: </strong> {token.symbol}
                            </div>

                            {token.external_url && (
                                <div className="description-item">
                                    <strong>External URL: </strong>
                                    <a
                                        href={token.external_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {token.external_url}
                                    </a>
                                </div>
                            )}

                            <div className="description-item">
                                <strong>Description: </strong>{" "}
                                {token.description}
                            </div>

                            <div className="description-item">
                                {token.collectionKey && (
                                    <>
                                        <strong>Collection: </strong>
                                        <a
                                            href={`https://solscan.io/token/${token.collectionKey}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {token.collectionKey}
                                        </a>
                                        <br />
                                    </>
                                )}
                            </div>

                            <div className="description-item">
                                <strong>Mint Address: </strong>
                                <a
                                    href={`https://solscan.io/token/${token.mint}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {token.mint}
                                </a>
                            </div>

                            <div className="description-item">
                                <strong>Metadata URI: </strong>
                                <a
                                    href={token.uri}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {token.uri}
                                </a>
                            </div>

                            {Array.isArray(token.attributes) &&
                            token.attributes.length > 0 && (
                                <div className="description-item">
                                    <strong>Attributes:</strong>
                                    <table className="token-attributes-table">
                                        <thead>
                                        <tr>
                                            <th>Trait</th>
                                            <th>Value</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {token.attributes.map(
                                            (attr, idx) => (
                                                <tr key={idx}>
                                                    <td>
                                                        {attr.trait_type ||
                                                        attr.traitType ||
                                                        "-"}
                                                    </td>
                                                    <td>
                                                        {String(
                                                            attr.value ??
                                                            ""
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        )}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {canBurn && (
                                <>
                                <div className="ownership-badge-container">
                                    <h5>⭐ I own this</h5>
                                </div>
                                </>
                                )}


                        </div>
                    </div>
                </div>

                {/* ACTIONS BELOW THE CARD */}
                <div className="token-detail-actions">
                    {burnInfo && (
                        <div className="burn-info">
                            <p>
                                <strong>Burn Status: </strong>
                                {burnInfo.status}
                            </p>
                            <p>
                                <strong>Token: </strong>
                                {burnInfo.tokenName}
                            </p>
                            {burnInfo.txId && (
                                <p>
                                    <strong>Transaction: </strong>
                                    <a
                                        href={`https://solscan.io/tx/${burnInfo.txId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {burnInfo.txId}
                                    </a>
                                </p>
                            )}
                            {burnInfo.error && (
                                <p>
                                    <strong>Error: </strong>
                                    {burnInfo.error}
                                </p>
                            )}
                        </div>
                    )}



                    <div className="token-share-bar">
                        <span className="token-share-label">
                            Share card:
                        </span>
                    </div>

                    <div className="token-detail-share-buttons">
                        <button
                            className="token-share-button"
                            onClick={() =>
                                handleShareTokenCard(token, "twitter")
                            }
                            disabled={sharingTokenMint === token.mint}
                        >
                            <span className="token-share-pill">X</span>
                            Twitter
                        </button>

                        <button
                            className="token-share-button"
                            onClick={() =>
                                handleShareTokenCard(token, "facebook")
                            }
                            disabled={sharingTokenMint === token.mint}
                        >
                            <span className="token-share-pill">f</span>
                            Facebook
                        </button>

                        <button
                            className="token-share-button"
                            onClick={() =>
                                handleShareTokenCard(token, "instagram")
                            }
                            disabled={sharingTokenMint === token.mint}
                        >
                            <span className="token-share-pill">IG</span>
                            Instagram
                        </button>

                        <button
                            className="token-share-button"
                            onClick={() =>
                                handleShareTokenCard(token, "threads")
                            }
                            disabled={sharingTokenMint === token.mint}
                        >
                            <span className="token-share-pill">@</span>
                            Threads
                        </button>

                        <button
                            className="token-share-button"
                            onClick={() => handleDownloadTokenCard(token)}
                            disabled={sharingTokenMint === token.mint}
                        >
                            <span className="token-share-pill">↓</span>
                            Download Card
                        </button>

                        <button
                            className="token-share-button"
                            onClick={() => handleCopyTokenCard(token)}
                            disabled={sharingTokenMint === token.mint}
                        >
                            <span className="token-share-pill">⧉</span>
                            Copy Card
                        </button>


                        <button
                            className="token-share-button"
                            onClick={handleCopyLink}
                        >
                            <span className="token-share-pill">🔗</span>
                            Copy Link
                        </button>
                    </div>

                    <button
                        className="token-detail-back-button"
                        onClick={() => navigate(-1)}
                    >
                        ← Back
                    </button>
                </div>

                {canBurn && (
                    <>
                        <div className="ownership-badge-container">
                            <h5>⭐ Digital Asset Owner</h5>
                        </div>
                        <h4>Burn this digital asset. Check the metadata to ensure that it isn't important!</h4>

                        <button
                            id="burn-button"
                            onClick={() => handleBurn(token)}
                        >
                            Burn
                        </button>

                    </>
                )}

                {modalImage && (
                    <ImageModal
                        image={modalImage.image}
                        name={modalImage.name}
                        onClose={() => setModalImage(null)}
                    />
                )}
            </div>
        </div>
    );
}

export default TokenDetail;
